import React, { Component } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import DateRangePicker from './components/DateRangePicker';
import DevicePicker from './components/DevicePicker';
import TagPicker from './components/TagPicker';
import OptionPicker from './components/OptionPicker';
import withQueryStringProps from '../../../../../../../../components/withQueryStringProps';
import demoMode from '../../../../../../../../services/demoMode';
import moment from 'moment';
import _ from 'lodash';
import allowedDevicesService from '../../../../../../../../services/allowedDevices';

/**
 * Get first answer date for the viewer (account owner or subuser)
 * @param {*} widgets 
 * @param {*} allowedDevices 
 * @param {*} isAccountAdmin
 * @returns 
 */
function getFirstAnswerDate(widgets = [], allowedDevices = [], isAccountAdmin = true) {
  let firstAnswerDate = moment().format('YYYY-MM-DD');
  try {

    if (demoMode()) {
      return moment().subtract(3, 'months').format('YYYY-MM-DD');
    }

    if (isAccountAdmin) {
      const firstAnswerDateForAccount = getFirstAnswerDateForAdmin(widgets);
      if (firstAnswerDateForAccount) {
        firstAnswerDate = moment(firstAnswerDateForAccount).format('YYYY-MM-DD');
      }
    } else {
      const firstAnswerDateForSubuser = getFirstAnswerDateWithinAllowedDevices(widgets, allowedDevices);
      if (firstAnswerDateForSubuser) {
        firstAnswerDate = moment(firstAnswerDateForSubuser).format('YYYY-MM-DD');
      }
    }

  } catch (e) {
    // Silencing the error and returning the default date to avoid breaking the UI
  }
  return firstAnswerDate;
}

/**
 * Find the earliest date of answer for the whole account
 * @param {*} widgets 
 * @returns 
 */
function getFirstAnswerDateForAdmin(widgets = []) {
  try {
    const firstAnswerTimes = widgets
      .map(widget => {
        return (
          widget &&
          widget.config &&
          widget.config.question &&
          widget.config.question.firstAnswerTime
        );
      })
      .filter(time => !_.isNil(time));

    let firstAnswerTime = firstAnswerTimes[0];
    firstAnswerTimes.forEach(time => {
      if (moment(firstAnswerTime).diff(moment(time)) > 0) {
        firstAnswerTime = time;
      }
    });

    return moment(firstAnswerTime).format('YYYY-MM-DD');
  } catch (e) {
    return null;
  }
}

/**
 * Find the earliest time of answer within the allowed devices 
 * @param {*} widgets 
 * @param {*} allowedDevices 
 */
function getFirstAnswerDateWithinAllowedDevices(widgets = [], allowedDevices = []) {
  try {
    const allowedDeviceIds = allowedDevices.map(d => d.id);
    const questionData = widgets.map(w => w.config.question ? w.config.question.firstAnswerTimeByDevice : null).filter(Boolean);

    return questionData.reduce((a, c) => {
      c.forEach((item) => {
        if (!a) {
          if (item.localTime && allowedDeviceIds.indexOf(item.globalId) !== -1) {
            a = item.localTime;
            return null;
          } else {
            return null;
          }
        }
        if (item.localTime && allowedDeviceIds.indexOf(item.globalId) !== -1 && moment(a).isAfter(item.localTime)) {
          a = item.localTime;
        }
      });
      return a;
    }, null);

  } catch (e) {
    return null;
  }
}

const styles = {
  wrapper: {
    backgroundColor: 'white',
    borderRadius: 2,
    overflow: 'auto',
    padding: '6px 24px 3px',
  },
  devicePickerWrapper: {
    float: 'left',
    paddingBottom: 2,
    paddingTop: 2,
  },
  dateRangePickerWrapper: {
    float: 'right',
    paddingBottom: 2,
    paddingRight: 2,
  },
};

@withQueryStringProps(['print', 'selectedTags'])
class ReportFilters extends Component {

  render() {
    const isAccountAdmin = this.props.viewer.roles.includes('account_admin');

    const allowedDevices = allowedDevicesService.getAllowedDevices(this.props.devices, this.props.viewer.devices, this.props.report.devices);
    // Setting start date from the date range picker
    const firstAnswerDate = getFirstAnswerDate(this.props.report.widgets, allowedDevices, isAccountAdmin);

    const { selectedTags, print } = this.props;

    const hasSelectedTags = selectedTags && !!(selectedTags.filter(Boolean)).length;

    const hasTags = !!(allowedDevices).find(d => d.tags && d.tags.length);

    const wrapperStyles = this.props.print
      ? Object.assign({}, styles.wrapper, { boxShadow: '0px 0px 0px 1px #ccc' })
      : styles.wrapper;

    const tagPickerWrapperStyles = (print ? hasSelectedTags : hasTags)
      ? Object.assign({}, wrapperStyles, { display: 'flex', flexDirection: 'Column', paddingBottom: 8, paddingTop: 8 })
      : { display: 'none' };

    return (
      <div>
        <div style={wrapperStyles}>
          <div style={styles.devicePickerWrapper}>
            <DevicePicker
              viewer={this.props.viewer}
              report={this.props.report}
              relayDevices={this.props.devices}
            />
          </div>
          {/* <div style={styles.devicePickerWrapper}>

          </div> */}
          <div style={styles.dateRangePickerWrapper}>
            <DateRangePicker
              report={this.props.report}
              firstDate={firstAnswerDate}
            />
          </div>
        </div>

        {(this.props.report.showTagsFilter && (print ? hasSelectedTags : hasTags)) &&
          <div style={tagPickerWrapperStyles}>
            <div style={{ height: '100%' }}>
              <TagPicker
                viewer={this.props.viewer}
                report={this.props.report}
                relayDevices={this.props.devices}
              />
            </div>
          </div>
        }

        {(this.props.report.filters && this.props.report.filters.length > 0) && (
          <div style={Object.assign({}, wrapperStyles, { display: 'flex', flexDirection: 'Column', paddingBottom: 8, paddingTop: 8 })}>

            <div>
              {this.props.report.filters.map((filter, i) => {
                return filter.type === 'question' ? (
                  <OptionPicker
                    key={i}
                    report={this.props.report}
                    filter={filter}
                    isAccountAdmin={isAccountAdmin}
                  />
                ) : (
                  <div key={i}>Unknown filter type</div>
                );
              })}
            </div>

          </div>
        )}
      </div>
    );
  }
}

export default createFragmentContainer(
  ReportFilters,
  graphql`
    fragment ReportFilters_viewer on Viewer {
      roles
      ...DevicePicker_viewer
      ...TagPicker_viewer
      devices {
        id
        tags
      }
    }
    fragment ReportFilters_report on Report {
      widgets {
        mongoId
        config {
          question {
            firstAnswerTime
            firstAnswerTimeByDevice {
              globalId
              deviceId
              localTime
              id
            }
            type
          }
          useNumbers
        }
      }
      filters {
        type
        locked
        ...OptionPicker_filter
      }
      ...DevicePicker_report
      ...DateRangePicker_report
      ...OptionPicker_report
      ...TagPicker_report
      devices {
        id
        tags
      }
      showTagsFilter
    }
    fragment ReportFilters_devices on DeviceConnection {
      ...DevicePicker_relayDevices
      ...TagPicker_relayDevices
      edges {
        node {
          id
          tags
        }
      }
    }
  `,
);



// WEBPACK FOOTER //
// ./src/scenes/Report/components/ReportWrapper/components/ReportHeader/components/ReportFilters/ReportFilters.js